import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { Table, Td, Th } from "@/components/ui/Table";
import { useToast } from "@/hooks/useToast";
import { formatDateTime } from "@/lib/format";
import { breakpoints, type BreakpointSet, type Disagreement } from "@/lib/phase2";
import { strings } from "@/lib/strings";

/**
 * Breakpoint tables (BP-10 … BP-13).
 *
 * Loading a table is a command-line step, deliberately: a clinical threshold
 * must come from a named published source, checked by someone qualified, not
 * typed into a web form. This screen activates a loaded set, re-interprets
 * stored measurements against it, and lists where the result disagrees with
 * what was entered.
 */
export function BreakpointsAdmin() {
  const { notify } = useToast();
  const [sets, setSets] = useState<BreakpointSet[]>([]);
  const [disagreements, setDisagreements] = useState<Disagreement[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setSets(await breakpoints.sets());
    } catch {
      notify(strings.errors.forbidden, "error");
    }
  }, [notify]);

  useEffect(() => {
    void load();
  }, [load]);

  const activate = async (set: BreakpointSet) => {
    setBusy(true);
    try {
      await breakpoints.activate(set.id);
      notify(
        `${set.standard} ${set.version} is now used to check new entries.`,
        "success",
      );
      await load();
    } catch {
      notify(strings.errors.generic, "error");
    } finally {
      setBusy(false);
    }
  };

  const reinterpret = async (set: BreakpointSet) => {
    setBusy(true);
    try {
      const summary = await breakpoints.reinterpret(set.id);
      notify(
        `Checked ${summary.isolates} isolate(s); ${summary.disagreements} disagreement(s) found.`,
        "success",
      );
      setSelected(set.id);
      setDisagreements(await breakpoints.disagreements(set.id));
    } catch {
      notify(strings.errors.generic, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Breakpoints</h1>
        <p className="text-sm text-slate-500">
          The thresholds that turn a zone diameter or MIC into S, I or R.
        </p>
      </div>

      <Card>
        <CardBody className="space-y-2 text-sm text-slate-700">
          <p>
            The platform ships no thresholds. A table is loaded from a published
            source on the command line, so that a clinical value always comes
            from a named version somebody has checked:
          </p>
          <pre className="overflow-x-auto rounded bg-slate-50 p-3 text-xs">
{`python -m scripts.import_breakpoints eucast-v14.csv \\
    --standard eucast --version v14.0 --year 2024 \\
    --source-url https://www.eucast.org/clinical_breakpoints --dry-run`}
          </pre>
          <p>
            Re-interpreting never changes what a volunteer entered. The
            recomputed values are stored separately, and where the two disagree
            it is listed below for you to judge.
          </p>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Loaded tables" />
        {sets.length === 0 ? (
          <EmptyState
            title="No breakpoint table loaded."
            description="Until one is loaded, measurements are stored but not interpreted, and Q-13 stays silent."
          />
        ) : (
          <Table caption="Breakpoint tables">
            <thead>
              <tr>
                <Th>Standard</Th>
                <Th>Version</Th>
                <Th className="text-right">Thresholds</Th>
                <Th>Loaded</Th>
                <Th>Source</Th>
                <Th>
                  <span className="sr-only">Actions</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {sets.map((set) => (
                <tr key={set.id} className="hover:bg-slate-50">
                  <Td className="uppercase">{set.standard}</Td>
                  <Td>
                    {set.version}
                    {set.year ? ` (${set.year})` : ""}
                    {set.is_active && (
                      <Badge tone="ok" className="ml-2">
                        in use
                      </Badge>
                    )}
                  </Td>
                  <Td className="text-right tabular-nums">{set.breakpoint_count}</Td>
                  <Td className="whitespace-nowrap text-xs">
                    {formatDateTime(set.imported_at)}
                  </Td>
                  <Td className="max-w-[14rem] truncate text-xs text-slate-500">
                    {set.source_url ?? "—"}
                  </Td>
                  <Td className="whitespace-nowrap">
                    <div className="flex gap-1">
                      {!set.is_active && (
                        <Button
                          size="sm"
                          variant="secondary"
                          loading={busy}
                          onClick={() => void activate(set)}
                        >
                          Use this one
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={busy}
                        onClick={() => void reinterpret(set)}
                      >
                        Re-check stored results
                      </Button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {selected && (
        <Card>
          <CardHeader
            title={`Disagreements (${disagreements.length})`}
            description="Where the measurement reads differently from the result entered. Either the register was misread, or the breakpoint has moved."
          />
          {disagreements.length === 0 ? (
            <EmptyState title="No disagreements. Every measurement matches what was entered." />
          ) : (
            <Table caption="Disagreements between measurement and entered result">
              <thead>
                <tr>
                  <Th>Record</Th>
                  <Th>Organism</Th>
                  <Th>Antibiotic</Th>
                  <Th>Entered</Th>
                  <Th>Measurement reads</Th>
                  <Th>Measured</Th>
                </tr>
              </thead>
              <tbody>
                {disagreements.flatMap((row) =>
                  row.disagreements.map((item) => (
                    <tr key={`${row.isolate_id}-${item.antibiotic}`}>
                      <Td className="font-mono text-xs">{row.record_id}</Td>
                      <Td className="italic">{row.organism_code ?? "—"}</Td>
                      <Td className="font-mono text-xs">{item.antibiotic}</Td>
                      <Td>
                        <Badge tone="warning">{item.entered}</Badge>
                      </Td>
                      <Td>
                        <Badge tone="info">{item.computed}</Badge>
                      </Td>
                      <Td className="text-xs text-slate-600">
                        {item.measurement} ({item.method})
                      </Td>
                    </tr>
                  )),
                )}
              </tbody>
            </Table>
          )}
        </Card>
      )}
    </div>
  );
}
